import { useState, useEffect } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import { addDays, startOfWeek, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import DayColumn from './DayColumn';
import Modal from '../UI/Modal';
import DeliveryForm from './DeliveryForm';
import DeliveryEditForm from './DeliveryEditForm';
import MyRoute from './MyRoute';
import DayView from './DayView';
import useCurrentUser from '../../hooks/useCurrentUser';
import useFrenchHolidays from '../../hooks/useFrenchHolidays';
import EventForm from './EventForm';
import useWeekType from '../../hooks/useWeekType';
import useAcl from '../../hooks/useAcl';
import api, { updateDeliveryPosition } from '../../services/api';

export default function PlanningGrid({ preferences = {}, checkAndUnlock, checkThresholds }) {
    const [currentWeek, setCurrentWeek] = useState(new Date());
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const [users, setUsers] = useState([]);
    const [dayInfos, setDayInfos] = useState([]);
    const [teamLocations, setTeamLocations] = useState([]);
    const [filtreLibreur, setFiltreLibreur] = useState('');
    const [showMyRoute, setShowMyRoute] = useState(false);
    const [vue, setVue] = useState('semaine');
    const [currentDay, setCurrentDay] = useState(new Date());
    const monday = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const days = Array.from({ length: 5 }, (_, i) => addDays(monday, i));
    const [filtrePathologie, setFiltrePathologie] = useState('');
    const [masquerPauseHospitalise, setMasquerPauseHospitalise] = useState(true);
    const [derivedLivreurColors, setDerivedLivreurColors] = useState({});
    const currentYear = currentWeek.getFullYear();
    const holidays = useFrenchHolidays(currentYear);
    // Si la semaine chevauche deux années (ex: 31 déc → 4 jan), couvrir les deux
    const holidaysNextYear = useFrenchHolidays(currentYear + 1);
    const allHolidays = new Set([...holidays, ...holidaysNextYear]);
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);   // event en édition
    const [showEventModal, setShowEventModal] = useState(false); // modal création
    const [newEventDate, setNewEventDate] = useState('');        // date pré-remplie
    const weekType = useWeekType(monday);
    const [editCountPerDelivery, setEditCountPerDelivery] = useState({});
    const [weekNavCount, setWeekNavCount] = useState(0);
    const [statutCountPerDelivery, setStatutCountPerDelivery] = useState({});
    const currentUser = useCurrentUser();
    const { can } = useAcl();

    useEffect(() => {
        loadUsers();
    }, []); // Une seule fois

    useEffect(() => {
        setLoading(true);

        const loadAll = async () => {
            try {
                const dateDebut = format(days[0], 'yyyy-MM-dd');
                const dateFin = format(days[4], 'yyyy-MM-dd');
                const res = await api.get(`/planning-data?dateAfter=${dateDebut}&dateBefore=${dateFin}`);
                setDeliveries(prev => res.data.deliveries || prev);
                setDayInfos(prev => res.data.dayInfos || prev);
                setTeamLocations(prev => res.data.teamLocations || prev);
                setEvents(prev => res.data.events || prev);
            } catch (err) {
                console.error('Erreur chargement planning', err);
            } finally {
                setLoading(false);
            }
        };

        loadAll();

        let eventSource = null;
        let reconnectTimeout = null;

        const connectSSE = () => {
            const token = localStorage.getItem('token');
            if (!token) return;

            eventSource = new EventSource(
                `https://api.aniervincent.fr/api/sse/planning?token=${token}`
            );

            eventSource.addEventListener('update', () => {
                loadAll();
            });

            eventSource.onerror = () => {
                eventSource.close();
                reconnectTimeout = setTimeout(() => {
                    connectSSE();
                }, 5000);
            };
        };

        connectSSE();

        return () => {
            if (eventSource) eventSource.close();
            if (reconnectTimeout) clearTimeout(reconnectTimeout);
        };
// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWeek]);

    const getDeliveriesForDay = (day) => {
        const dateStr = format(day, 'yyyy-MM-dd');
        return deliveries
            .filter(d => {
                const dateMatch = d.date?.startsWith(dateStr);
                const livreurMatch = !filtreLibreur || d.livreur?.['@id'] === filtreLibreur;
                const fttMatch = d.patient?.statutPatient !== 'ftt';
                const pathologieMatch = !filtrePathologie || d.patient?.pathologie === filtrePathologie;
                const pauseHospitaliseMatch = !masquerPauseHospitalise || d.patient?.statutPatient !== 'en_pause';
                return dateMatch && livreurMatch && fttMatch && pathologieMatch && pauseHospitaliseMatch;
            })
            .sort((a, b) => {
                const livreurA = a.livreur?.nom || '';
                const livreurB = b.livreur?.nom || '';
                if (livreurA !== livreurB) return livreurA.localeCompare(livreurB);

                // À livreur égal, la rétro passe en tête
                const retroA = a.patient?.isRetro ? 0 : 1;
                const retroB = b.patient?.isRetro ? 0 : 1;
                if (retroA !== retroB) return retroA - retroB;

                if (a.heureDebut && b.heureDebut) return a.heureDebut.localeCompare(b.heureDebut);
                if (a.heureDebut) return -1;
                if (b.heureDebut) return 1;
                return ((a.position || 0) - (b.position || 0)) || (a.id - b.id);
            });
    };

// Remplacer loadUsers par :
    const loadUsers = async () => {
        try {
            const res = await api.get('/users');
            const usersList = res.data.member || [];
            setUsers(usersList);
            const map = {};
            usersList.forEach(u => {
                const iri = u['@id'] || `/api/users/${u.id}`;
                const color = u.preferences?.livreurColors?.[iri] || u.preferences?.livreurColors?.[u['@id']];
                if (color) {
                    map[iri] = color;
                    map[`/api/users/${u.id}`] = color;
                }
            });
            setDerivedLivreurColors(map);
        } catch (err) {
            console.error(err);
        }
    };

    const loadDayInfos = async () => {
        try {
            const dateDebut = format(days[0], 'yyyy-MM-dd');
            const dateFin = format(days[4], 'yyyy-MM-dd');
            const res = await api.get(`/day_infos?date[after]=${dateDebut}&date[before]=${dateFin}`);
            setDayInfos(res.data.member || []);
        } catch (err) {
            console.error(err);
        }
    };

    const loadTeamLocations = async () => {
        try {
            const dateDebut = format(days[0], 'yyyy-MM-dd');
            const dateFin = format(days[4], 'yyyy-MM-dd');
            const res = await api.get(`/team_locations?date[after]=${dateDebut}&date[before]=${dateFin}`);
            setTeamLocations(res.data.member || []);
        } catch (err) {
            console.error(err);
        }
    };

    const loadDeliveries = async () => {
        // Même source que le chargement initial : planning-data uniquement
        try {
            const dateDebut = format(days[0], 'yyyy-MM-dd');
            const dateFin = format(days[4], 'yyyy-MM-dd');
            const res = await api.get(`/planning-data?dateAfter=${dateDebut}&dateBefore=${dateFin}`);
            setDeliveries(prev => res.data.deliveries || prev);
        } catch (err) {
            console.error('Erreur chargement livraisons', err);
        }
    };

    const loadEvents = async () => {
        try {
            const dateDebut = format(days[0], 'yyyy-MM-dd');
            const dateFin = format(days[4], 'yyyy-MM-dd');
            const res = await api.get(`/events?date[after]=${dateDebut}&date[before]=${dateFin}`);
            setEvents(res.data.member || []);
        } catch (err) {
            console.error('Erreur chargement events', err);
        }
    };

    const updatePrepStatut = async (deliveryId, newStatut) => {
        try {
            await api.patch(`/deliveries/${deliveryId}`, {
                statutPrep: newStatut,
            }, {
                headers: { 'Content-Type': 'application/merge-patch+json' }
            });
            loadDeliveries();

            const newCount = (statutCountPerDelivery[deliveryId] || 0) + 1;
            setStatutCountPerDelivery(prev => ({ ...prev, [deliveryId]: newCount }));
            if (newCount >= 5) checkAndUnlock && checkAndUnlock('indecis');

            if (newStatut === 'prepare') {
                checkAndUnlock && checkAndUnlock('premier_pas');
                checkThresholds && checkThresholds();
            }
        } catch (err) {
            console.error('Erreur mise à jour prep', err);
        }
    };

    const updateReportPending = async (deliveryId, value) => {
        try {
            await api.patch(`/deliveries/${deliveryId}`, {
                reportPending: value,
            }, {
                headers: { 'Content-Type': 'application/merge-patch+json' }
            });
            loadDeliveries();
        } catch (err) {
            console.error('Erreur mise à jour compte rendu', err);
        }
    };

    const handleEventDelete = async (event) => {
        try {
            const id = event.id || event['@id']?.split('/').pop();
            await api.delete(`/events/${id}`);
            loadEvents();
        } catch (err) {
            console.error(err);
        }
    };

    const onDragEnd = async (result) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;
        if (destination.droppableId === source.droppableId &&
            destination.index === source.index) return;

        // Parse source et destination
        const srcParts = source.droppableId.split('-');
        const dstParts = destination.droppableId.split('-');
        const srcPeriode = srcParts[3];
        const dstPeriode = dstParts[3];
        const dstDate = `${dstParts[0]}-${dstParts[1]}-${dstParts[2]}`;

        // Trouve la livraison déplacée
        const delivery = deliveries.find(d => String(d.id) === draggableId);
        if (!delivery) return;

        // Si la période change → ouvre la modale pour corriger l'heure
        if (srcPeriode !== dstPeriode) {
            setSelectedDelivery({
                ...delivery,
                date: dstDate,
                periode: dstPeriode,
                // Reset l'heure selon la période
                heureDebut: dstPeriode === 'matin' ? '1970-01-01T09:00:00+00:00' : '1970-01-01T14:00:00+00:00',
            });
            return;
        }

        // Même période → mise à jour silencieuse
        const updated = deliveries.map(d => {
            if (String(d.id) === draggableId) {
                return { ...d, date: dstDate, periode: dstPeriode, position: destination.index };
            }
            return d;
        });
        setDeliveries(updated);

        try {
            await updateDeliveryPosition(draggableId, {
                date: dstDate,
                periode: dstPeriode,
                position: destination.index,
            });
        } catch (err) {
            console.error('Erreur mise à jour position', err);
            loadDeliveries();
        }
    };

    const toggleAlerte = async (delivery) => {
        try {
            await api.patch(`/deliveries/${delivery.id}`, {
                alerte: !delivery.alerte,
            }, {
                headers: { 'Content-Type': 'application/merge-patch+json' }
            });
            loadDeliveries();
        } catch (err) {
            console.error(err);
        }
    };

    const aReclasserCount = deliveries.filter(d =>
        d.statutPrep === 'confirme' &&
        !(d.patient?.nom || '').toUpperCase().includes('RETRO')
    ).length;

    return (
        <div style={{padding: '24px'}}>

            {/* Header */}
            <div style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '16px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
            }}>
                {/* Ligne 1 — Navigation */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                }}>
                    <button onClick={() => {
                        const newWeek = addDays(currentWeek, -7);
                        setCurrentWeek(newWeek);
                        setCurrentDay(startOfWeek(newWeek, {weekStartsOn: 1}));
                        const newCount = weekNavCount + 1;
                        setWeekNavCount(newCount);
                        if (newCount >= 20) checkAndUnlock && checkAndUnlock('indecis_calendrier');
                    }} style={navButtonStyle}>
                        ← Semaine précédente
                    </button>

                    <h2 style={{margin: 0, fontSize: '18px', fontWeight: '700', color: 'var(--text-main)'}}>
                        Semaine du {format(monday, 'd MMMM yyyy', {locale: fr})}
                        <span style={{
                            marginLeft: '10px',
                            padding: '3px 10px',
                            borderRadius: '6px',
                            backgroundColor: '#ede9fe',
                            color: 'var(--accent-color)',
                            fontSize: '13px',
                            fontWeight: '700',
                            verticalAlign: 'middle',
                        }}>Semaine {weekType}</span>
                    </h2>

                    <button onClick={() => {
                        const newWeek = addDays(currentWeek, 7);
                        setCurrentWeek(newWeek);
                        setCurrentDay(startOfWeek(newWeek, {weekStartsOn: 1}));
                        const newCount = weekNavCount + 1;
                        setWeekNavCount(newCount);
                        if (newCount >= 20) checkAndUnlock && checkAndUnlock('indecis_calendrier');
                    }} style={navButtonStyle}>
                        Semaine suivante →
                    </button>
                </div>

                {/* Ligne 2 — Actions */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border-light)',
                }}>

                    {can('creer_livraison') && (
                        <button onClick={() => setShowModal(true)} style={{
                            padding: '7px 14px',
                            backgroundColor: 'var(--accent-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap',
                        }}>
                            <i className="ti ti-plus" style={{ marginRight: '6px' }} />Nouvelle livraison
                        </button>
                    )}

                    {/* Switch vue */}
                    <div style={{
                        display: 'flex',
                        backgroundColor: 'var(--hover-bg)',
                        borderRadius: '8px',
                        padding: '3px',
                        gap: '2px',
                    }}>
                        {[
                            { id: 'semaine', icon: 'ti-calendar-week', label: 'Semaine' },
                            { id: 'jour', icon: 'ti-calendar-day', label: 'Jour' },
                        ].map(v => (
                            <button
                                key={v.id}
                                onClick={() => setVue(v.id)}
                                style={{
                                    padding: '5px 12px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    backgroundColor: vue === v.id ? 'var(--card-bg)' : 'transparent',
                                    color: vue === v.id ? 'var(--text-main)' : 'var(--text-muted)',
                                    fontWeight: vue === v.id ? '700' : '400',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    boxShadow: vue === v.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <i className={`ti ${v.icon}`} style={{ marginRight: '4px' }} />{v.label}
                            </button>
                        ))}
                    </div>

                    {/* Filtres */}
                    <select
                        value={filtreLibreur}
                        onChange={e => setFiltreLibreur(e.target.value)}
                        style={{
                            padding: '7px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-default)',
                            fontSize: '13px',
                            backgroundColor: filtreLibreur ? '#ede9fe' : 'var(--input-bg)',
                            color: filtreLibreur ? 'var(--accent-color)' : 'var(--text-muted)',
                            cursor: 'pointer',
                        }}
                    >
                        <option value="">👤 Tous livreurs</option>
                        {users.map(u => (
                            <option key={u['@id']} value={u['@id']}>{u.nom}</option>
                        ))}
                    </select>

                    <select
                        value={filtrePathologie}
                        onChange={e => setFiltrePathologie(e.target.value)}
                        style={{
                            padding: '7px 10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-default)',
                            fontSize: '13px',
                            backgroundColor: filtrePathologie ? '#ede9fe' : 'var(--input-bg)',
                            color: filtrePathologie ? 'var(--accent-color)' : 'var(--text-muted)',
                            cursor: 'pointer',
                        }}
                    >
                        <option value="">🏥 Pathologies</option>
                        <option value="nutrition">🟢 Nutrition</option>
                        <option value="perfusion">🔵 Perfusion</option>
                        <option value="parkinson">⚫ Parkinson</option>
                    </select>

                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '7px 10px',
                        borderRadius: '8px',
                        border: '1px solid var(--border-default)',
                        fontSize: '13px',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                    }}>
                        <input
                            type="checkbox"
                            checked={masquerPauseHospitalise}
                            onChange={e => {
                                setMasquerPauseHospitalise(e.target.checked);
                                checkAndUnlock && checkAndUnlock('aigle_royal_en_pause');
                            }}
                            style={{cursor: 'pointer'}}
                        />
                        Masquer pause
                    </label>

                    {/* Droite */}
                    <div style={{marginLeft: 'auto', display: 'flex', gap: '8px'}}>
                        <button
                            onClick={() => {
                                setCurrentWeek(new Date());
                                setCurrentDay(new Date());
                            }}
                            style={{
                                padding: '7px 12px',
                                backgroundColor: 'var(--card-bg)',
                                border: '1px solid var(--border-default)',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '500',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <i className="ti ti-calendar" style={{ marginRight: '4px' }} />Aujourd'hui
                        </button>

                        <button
                            onClick={() => setShowMyRoute(true)}
                            style={{
                                padding: '7px 12px',
                                backgroundColor: '#16a34a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <i className="ti ti-map-2" style={{ marginRight: '4px' }} />Mon trajet
                        </button>
                    </div>
                </div>
            </div>

            {/* Barre info */}
            <div style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '12px',
                padding: '12px 20px',
                marginBottom: '12px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}><span style={{fontSize: '15px', fontWeight: '700', color: 'var(--text-main)'}}>
        <i className="ti ti-hand-stop"
           style={{marginRight: '4px'}}/> Bonjour {useCurrentUser()?.nom?.split(' ')[0]} !</span>
                <div style={{display: 'flex', gap: '20px', alignItems: 'center' }}><span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            <i className="ti ti-clipboard-list" style={{ marginRight: '4px' }} /> <strong style={{ color: 'var(--text-main)' }}>
                {deliveries.filter(d => d.statutPrep === 'a_contacter' || d.statutPrep === 'en_attente').length}
            </strong> livraison{deliveries.filter(d => d.statutPrep === 'a_contacter' || d.statutPrep === 'en_attente').length > 1 ? 's' : ''} à gérer cette semaine</span>
                    <span style={{ fontSize: '13px', color: '#d97706' }}>
            <i className="ti ti-refresh" style={{ marginRight: '4px' }} /> <strong style={{ color: '#d97706' }}>{aReclasserCount}</strong> à repasser en "préparer"</span>
                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            <i className="ti ti-truck" style={{ marginRight: '4px' }} /> <strong style={{ color: 'var(--text-main)' }}>
                {deliveries.filter(d => d.patient?.statutPatient !== 'ftt').length}
            </strong> livraison{deliveries.filter(d => d.patient?.statutPatient !== 'ftt').length > 1 ? 's' : ''} au total cette semaine</span>
                </div>
            </div>


            {/* Grille */}
            {loading ? (
                <div style={{textAlign: 'center', padding: '60px', color: '#888'}}>Chargement...</div>
            ) : vue === 'semaine' ? (
                <DragDropContext onDragEnd={onDragEnd}>
                    <div style={{display: 'flex', gap: '12px', alignItems: 'flex-start'}}>
                        {days.map(day => (
                            <DayColumn
                                key={day.toISOString()}
                                day={day}
                                deliveries={getDeliveriesForDay(day)}
                                onCardClick={(delivery) => {
                                    setSelectedDelivery(delivery);
                                    if (delivery.statutPrep === 'annule') checkAndUnlock && checkAndUnlock('chasseur_fantomes');
                                }}
                                onPrepChange={updatePrepStatut}
                                users={users}
                                dayInfo={dayInfos.find(d => d.date?.startsWith(format(day, 'yyyy-MM-dd'))) || null}
                                teamLocations={teamLocations.filter(t => t.date?.startsWith(format(day, 'yyyy-MM-dd')))}
                                onReload={() => {
                                    loadDayInfos();
                                    loadTeamLocations();
                                }}
                                livreurColors={derivedLivreurColors}
                                onPause={() => {
                                }}
                                holidays={allHolidays}
                                onReportToggle={(deliveryId, value) => updateReportPending(deliveryId, value)}
                                events={events.filter(e => e.date?.startsWith(format(day, 'yyyy-MM-dd')))}
                                onEventClick={(ev) => {
                                    setSelectedEvent(ev);
                                    setShowEventModal(true);
                                }}
                                onEventDelete={handleEventDelete}
                                onAddEvent={(dateStr) => {
                                    setNewEventDate(dateStr);
                                    setSelectedEvent(null);
                                    setShowEventModal(true);
                                }}
                            />
                        ))}
                    </div>
                </DragDropContext>
            ) : (
                <DayView
                    day={currentDay}
                    onDayChange={setCurrentDay}
                    deliveries={deliveries}
                    users={users}
                    dayInfos={dayInfos}
                    teamLocations={teamLocations}
                    livreurColors={derivedLivreurColors}
                    onCardClick={(delivery) => {
                        setSelectedDelivery(delivery);
                        if (delivery.statutPrep === 'annule') checkAndUnlock && checkAndUnlock('chasseur_fantomes');
                    }}
                    onPrepChange={updatePrepStatut}
                    onReload={() => {
                        loadDayInfos();
                        loadTeamLocations();
                    }}
                    filtreLibreur={filtreLibreur}
                    onDragEnd={onDragEnd}
                    masquerPauseHospitalise={masquerPauseHospitalise}
                />
            )}

            {/* Modale nouvelle livraison */}
            {showModal && (
                <Modal title="Nouvelle livraison" onClose={() => setShowModal(false)} gradient={true}>
                    <DeliveryForm
                        onSave={() => {
                            setShowModal(false);
                            loadDeliveries();
                        }}
                        onCancel={() => setShowModal(false)}
                        initialDate={format(new Date(), 'yyyy-MM-dd')}
                        initialPeriode="matin"
                    />
                </Modal>
            )}

            {/* Modale modification livraison */}
            {selectedDelivery && (
                <Modal
                    title="Modifier la livraison"
                    onClose={() => setSelectedDelivery(null)}
                    gradient={true}
                    headerAction={
                        can('bouton_alerte') ? (
                            <button
                                onClick={() => toggleAlerte(selectedDelivery)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '22px',
                                    cursor: 'pointer',
                                    color: selectedDelivery?.alerte ? '#ef4444' : '#ccc',
                                    lineHeight: 1,
                                }}
                                title={selectedDelivery?.alerte ? 'Alerte active — cliquer pour désactiver' : 'Signaler un problème SAP'}
                            >
                                ⚠️
                            </button>
                        ) : null
                    }
                >
                    <DeliveryEditForm
                        delivery={selectedDelivery}
                        can={can}
                        onSave={() => {
                            const id = selectedDelivery.id;
                            const newCount = (editCountPerDelivery[id] || 0) + 1;
                            setEditCountPerDelivery(prev => ({ ...prev, [id]: newCount }));
                            if (newCount >= 3) checkAndUnlock && checkAndUnlock('perfectionniste');
                            setSelectedDelivery(null);
                            loadDeliveries();
                        }}
                        onCancel={() => setSelectedDelivery(null)}
                        onDelete={() => {
                            setSelectedDelivery(null);
                            loadDeliveries();
                        }}
                    />
                </Modal>
            )}

            {/* Modale événement libre */}
            {showEventModal && (
                <Modal
                    title={selectedEvent ? "Modifier l'événement" : "Nouvel événement"}
                    onClose={() => {
                        setShowEventModal(false);
                        setSelectedEvent(null);
                    }}
                >
                    <EventForm
                        event={selectedEvent}
                        initialDate={newEventDate || format(new Date(), 'yyyy-MM-dd')}
                        initialPeriode="matin"
                        users={users}
                        onSave={() => {
                            setShowEventModal(false);
                            setSelectedEvent(null);
                            loadEvents();
                        }}
                        onCancel={() => {
                            setShowEventModal(false);
                            setSelectedEvent(null);
                        }}
                        onDelete={() => {
                            setShowEventModal(false);
                            setSelectedEvent(null);
                            loadEvents();
                        }}
                    />
                </Modal>
            )}

            {/* Modale trajet */}
            {showMyRoute && <MyRoute onClose={() => setShowMyRoute(false)}/>}

        </div>
    );
}

const navButtonStyle = {
    padding: '8px 16px',
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
};